// frontend/src/features/tbs/components/CollectorCatalog.jsx
// Right-hand drawer listing every collector by region with live status.
import React, { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Search, RefreshCw } from "lucide-react";
import { REGION_ORDER } from "../regions";

const STATUS_LABEL = { live: "Live", degraded: "Degraded", unavailable: "Unavailable", coming_soon: "Coming soon" };

export function StatusDot({ status }) {
  return <span className={`tbs-dot ${status || "coming_soon"}`} title={STATUS_LABEL[status] || status} />;
}

export default function CollectorCatalog({ open, onClose, collectors, onPick, isAdmin, onProbe, probing }) {
  const [q, setQ] = useState("");

  const groups = useMemo(() => {
    const norm = (s) => String(s || "").toLowerCase().replace(/[^a-z0-9]/g, "");
    const nq = norm(q);
    const filtered = collectors.filter((c) => !nq || norm(c.name).includes(nq) || norm(c.key).includes(nq) || norm(c.region).includes(nq));
    const map = new Map();
    for (const c of filtered) {
      const r = c.region || "Other / Special";
      if (!map.has(r)) map.set(r, []);
      map.get(r).push(c);
    }
    const order = [...REGION_ORDER, ...[...map.keys()].filter((r) => !REGION_ORDER.includes(r))];
    return order.filter((r) => map.has(r)).map((r) => ({ region: r, items: map.get(r).sort((a, b) => a.name.localeCompare(b.name)) }));
  }, [collectors, q]);

  const counts = useMemo(() => {
    const c = { live: 0, degraded: 0, unavailable: 0, coming_soon: 0 };
    collectors.forEach((x) => { c[x.status] = (c[x.status] || 0) + 1; });
    return c;
  }, [collectors]);

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div className="tbs-overlay" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose} />
          <motion.aside className="tbs-drawer" initial={{ x: 40, opacity: 0 }} animate={{ x: 0, opacity: 1 }} exit={{ x: 40, opacity: 0 }} transition={{ type: "spring", stiffness: 320, damping: 30 }}>
            <div className="tbs-drawer-hd">
              <h2>Collectors</h2>
              {isAdmin && (
                <button className="tbs-btn sm" onClick={onProbe} disabled={probing} title="Fetch one known statement from each fast collector and record the result">
                  <RefreshCw size={12} className={probing ? "tbs-spin" : ""} /> {probing ? "Probing…" : "Run health check"}
                </button>
              )}
              <button className="tbs-x" onClick={onClose} aria-label="Close"><X size={12} /></button>
            </div>
            <div className="tbs-drawer-bd">
              <div className="tbs-in" style={{ display: "flex", alignItems: "center", gap: 8, padding: "7px 10px" }}>
                <Search size={13} color="#8E96AD" />
                <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by county, key, or region…" autoFocus
                  style={{ border: "none", background: "none", outline: "none", flex: 1, fontSize: 12 }} />
                {q && <button className="tbs-x" onClick={() => setQ("")}><X size={10} /></button>}
              </div>
              <div className="tbs-legend">
                <span><i className="tbs-dot live" /> Live {counts.live}</span>
                <span><i className="tbs-dot degraded" /> Degraded {counts.degraded}</span>
                <span><i className="tbs-dot unavailable" /> Unavailable {counts.unavailable}</span>
                <span><i className="tbs-dot coming_soon" /> Coming soon {counts.coming_soon}</span>
              </div>
              {groups.length === 0 && <p className="tbs-help">No collectors match.</p>}
              {groups.map((g) => (
                <div key={g.region}>
                  <div className="tbs-region">{g.region}</div>
                  {g.items.map((c) => (
                    <button key={c.key} className="tbs-cat-item" disabled={!c.selectable} onClick={() => { onPick(c); onClose(); }}
                      title={c.selectable ? `Use ${c.name}` : c.status_note || STATUS_LABEL[c.status]}>
                      <StatusDot status={c.status} />
                      <span className="nm">{c.name}</span>
                      <span className={`tbs-pill ${c.status === "live" ? "ok" : c.status === "degraded" ? "warn" : c.status === "unavailable" ? "err" : "mute"}`}>
                        {STATUS_LABEL[c.status] || c.status}{c.last_checked ? "" : ""}
                      </span>
                      <span className="idt">{c.id_type} · e.g. {c.id_example} · {c.id_hint}{c.expected_seconds >= 45 ? ` · ~${c.expected_seconds}s each` : ""}</span>
                      {c.status_note && c.status !== "live" && <span className={`note ${c.status === "unavailable" ? "bad" : ""}`}>{c.status_note}</span>}
                    </button>
                  ))}
                </div>
              ))}
            </div>
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}
